import { useEffect, useRef } from 'react';

import { useLocation } from '@remix-run/react';

declare global {
  interface Window {
    gtag: any;
  }
}

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
}

const GAPixel = ({ pixelId, marketingCookies }: PixelProps) => {
  const location = useLocation();
  const script1Ref = useRef<HTMLScriptElement | null>(null);
  const script2Ref = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  // Fire page view on each route change
  useEffect(() => {
    try {
      if (window.gtag) {
        window.gtag('config', pixelId?.trim(), {
          page_path: location.pathname,
        });
      }
    } catch {
      /*swallow exception*/
    }
  }, [location, pixelId]);

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        // match valid pixel id: UA- followed by 6+ digits, optional - and digit
        if (!trimmedId?.match(/^UA-\d{6,}(-\d)?$/) || !marketingCookies) {
          return;
        }

        // Prevent duplicate script injection
        if (document.getElementById('ga-pixel')) {
          return;
        }

        const script1 = document.createElement('script');
        script1.id = 'ga-pixel-loader';
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${trimmedId}`;
        document.head.appendChild(script1);
        script1Ref.current = script1;

        const script2 = document.createElement('script');
        script2.async = true;
        script2.id = 'ga-pixel';
        script2.innerHTML = `
          (function(){try{
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          }catch{/*swallow exception*/}})();
        `;
        document.head.appendChild(script2);
        script2Ref.current = script2;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function for both scripts
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (script1Ref.current?.parentNode) {
          script1Ref.current.parentNode.removeChild(script1Ref.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
      try {
        if (script2Ref.current?.parentNode) {
          script2Ref.current.parentNode.removeChild(script2Ref.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [pixelId, marketingCookies]);

  return null;
};

export default GAPixel;
